<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { DEFAULT_REDIRECT_URL } from 'dashboard/constants/globals';
import { setNewPassword } from '../../../api/auth';

const props = defineProps({
  resetPasswordToken: { type: String, default: '' },
});

const router = useRouter();

const MIN_PASSWORD_LENGTH = 8;
const SUCCESS_REDIRECT_DELAY_MS = 1400;

const status = ref('edit');
const credentials = reactive({
  password: '',
  passwordConfirm: '',
});
const showPassword = ref(false);
const showPasswordConfirm = ref(false);
const isLoading = ref(false);

const passwordChecks = computed(() => {
  const v = credentials.password || '';
  const c = credentials.passwordConfirm || '';
  return [
    { key: 'length', label: `At least ${MIN_PASSWORD_LENGTH} characters`, ok: v.length >= MIN_PASSWORD_LENGTH },
    { key: 'number', label: 'At least 1 number', ok: /[0-9]/.test(v) },
    { key: 'symbol', label: 'At least 1 symbol (e.g. ! ? @ # …)', ok: /[^a-zA-Z0-9]/.test(v) },
    { key: 'match', label: 'Both passwords match', ok: v.length > 0 && c.length > 0 && v === c },
  ];
});

const passwordStrength = computed(() => {
  const v = credentials.password || '';
  if (!v.length) return 'none';
  const passed = passwordChecks.value.slice(0, 3).filter(c => c.ok).length;
  let score = passed;
  if (v.length >= 12) score++;
  if (v.length >= 16) score++;
  if (score <= 1) return 'weak';
  if (score <= 3) return 'medium';
  return 'strong';
});

const canSubmit = computed(
  () => passwordChecks.value.every(c => c.ok) && !isLoading.value
);

const submitNewPassword = async () => {
  if (!canSubmit.value) return;
  isLoading.value = true;
  try {
    await setNewPassword({
      resetPasswordToken: props.resetPasswordToken,
      password: credentials.password,
      confirmPassword: credentials.passwordConfirm,
    });
    status.value = 'success';
    setTimeout(() => {
      window.location = DEFAULT_REDIRECT_URL;
    }, SUCCESS_REDIRECT_DELAY_MS);
  } catch (error) {
    const msg =
      error?.response?.data?.errors?.full_messages?.[0] ||
      error?.message ||
      'Could not update your password. The reset link may have expired.';
    useAlert(msg);
  } finally {
    isLoading.value = false;
  }
};

const goToSignIn = () => {
  router.push({ path: '/app/login' });
};

onMounted(() => {
  if (!props.resetPasswordToken) {
    useAlert('That reset link is missing its token. Please request a new one.');
    router.push({ path: '/app/auth/reset/password' });
  }
});
</script>

<template>
  <div class="cc-reset-page">
    <div class="cc-reset-noise" />

    <header class="cc-reset-header">
      <div class="cc-brand-mark">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="38"
          height="38"
          viewBox="0 0 96 92"
          class="cc-brand-icon"
        >
          <defs>
            <linearGradient id="goldgrad-snp" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#d8b86a" />
              <stop offset="0.5" stop-color="#a8842f" />
              <stop offset="1" stop-color="#7a5a14" />
            </linearGradient>
            <clipPath id="clipover-snp">
              <rect x="38" y="44" width="20" height="22" />
            </clipPath>
          </defs>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(7,11)"
            fill="none"
            stroke="#0d9488"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(7,11)" fill="#0d9488">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 32 36 L 37 47 L 20 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(41,30)"
            fill="none"
            stroke="url(#goldgrad-snp)"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(41,30)" fill="#a8842f">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <g clip-path="url(#clipover-snp)">
            <path
              d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
              transform="translate(7,11)"
              fill="none"
              stroke="#0d9488"
              stroke-width="7"
              stroke-linejoin="round"
            />
          </g>
        </svg>
        <div class="cc-brand-text">
          <div class="cc-wordmark">
            <span class="cc-wordmark-part cc-wordmark-creator">Creator</span><span class="cc-wordmark-dash">-</span><span class="cc-wordmark-part cc-wordmark-chat">Chat</span><span class="cc-wordmark-tld">.com</span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="90"
            height="2"
            viewBox="0 0 90 2"
            class="cc-brand-divider"
          >
            <defs>
              <linearGradient id="tealfade-snp" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#0d9488" stop-opacity="0" />
                <stop offset="20%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="80%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="100%" stop-color="#0d9488" stop-opacity="0" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="90" height="2" fill="url(#tealfade-snp)" />
          </svg>
          <div class="cc-tagline">Conversations · Tailored</div>
        </div>
      </div>
    </header>

    <main class="cc-reset-main">
      <!-- EDIT -->
      <div v-if="status === 'edit'" class="cc-auth-stack">
        <div class="cc-auth-card">
          <h1 class="cc-h-display">Set a new password</h1>
          <p class="cc-sub">Choose a strong password to secure your account.</p>

          <form class="cc-form" @submit.prevent="submitNewPassword">
            <div class="cc-field">
              <label class="cc-label">New password <span class="cc-req">*</span></label>
              <div class="cc-pwd-wrap">
                <input
                  v-model="credentials.password"
                  :type="showPassword ? 'text' : 'password'"
                  class="cc-input cc-input--pwd"
                  placeholder="Choose a strong password"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="cc-pwd-toggle"
                  @click="showPassword = !showPassword"
                >
                  {{ showPassword ? 'Hide' : 'Show' }}
                </button>
              </div>
              <div
                v-if="credentials.password.length > 0"
                class="cc-pwd-strength"
                :class="`cc-pwd-strength--${passwordStrength}`"
              >
                <div /><div /><div />
              </div>
            </div>

            <div class="cc-field">
              <label class="cc-label">Confirm new password <span class="cc-req">*</span></label>
              <div class="cc-pwd-wrap">
                <input
                  v-model="credentials.passwordConfirm"
                  :type="showPasswordConfirm ? 'text' : 'password'"
                  class="cc-input cc-input--pwd"
                  :class="{
                    'cc-input--match':
                      credentials.passwordConfirm.length > 0 &&
                      credentials.passwordConfirm === credentials.password,
                  }"
                  placeholder="Re-enter password"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="cc-pwd-toggle"
                  @click="showPasswordConfirm = !showPasswordConfirm"
                >
                  {{ showPasswordConfirm ? 'Hide' : 'Show' }}
                </button>
              </div>
            </div>

            <ul class="cc-pwd-rules">
              <li
                v-for="rule in passwordChecks"
                :key="rule.key"
                :class="{ 'cc-pwd-rule--ok': rule.ok }"
              >
                <svg
                  v-if="rule.ok"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <svg
                  v-else
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="9" />
                </svg>
                <span>{{ rule.label }}</span>
              </li>
            </ul>

            <button
              type="submit"
              class="cc-btn-primary"
              :disabled="!canSubmit"
            >
              <template v-if="isLoading">Updating password…</template>
              <template v-else>Update password</template>
            </button>
          </form>

          <div class="cc-info-panel">
            After updating, you'll be signed out of all other sessions on every device.
          </div>

          <div class="cc-card-foot">
            <a href="#" class="cc-link-accent" @click.prevent="goToSignIn">
              ← Back to sign in
            </a>
          </div>
        </div>
      </div>

      <!-- SUCCESS -->
      <div v-else class="cc-auth-stack">
        <div class="cc-auth-card cc-auth-card--centered">
          <div class="cc-status-icon cc-status-icon--accent-solid">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1 class="cc-h-display">Password updated</h1>
          <p class="cc-sub">Signing you in…</p>
          <div class="cc-success-spinner">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0f766e" stroke-width="2.5" stroke-linecap="round" class="cc-spinner">
              <line x1="12" y1="2" x2="12" y2="6" />
              <line x1="12" y1="18" x2="12" y2="22" />
              <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
              <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
              <line x1="2" y1="12" x2="6" y2="12" />
              <line x1="18" y1="12" x2="22" y2="12" />
              <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
              <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
            </svg>
          </div>
        </div>
      </div>
    </main>

    <footer class="cc-site-footer" role="contentinfo">
      <div class="cc-site-footer-links">
        <a href="https://creator-chat.com/#features" target="_blank" rel="noopener">Features</a>
        <a href="https://creator-chat.com/#fangate" target="_blank" rel="noopener">Fangate</a>
        <a href="https://creator-chat.com/#pricing" target="_blank" rel="noopener">Pricing</a>
        <a href="https://creator-chat.com/faq.html" target="_blank" rel="noopener">FAQ</a>
        <a href="mailto:info@creator-chat.com">Contact</a>
        <a href="https://creator-chat.com/imprint.html" target="_blank" rel="noopener">Imprint</a>
        <a href="https://creator-chat.com/privacy.html" target="_blank" rel="noopener">Privacy</a>
        <a href="https://creator-chat.com/terms.html" target="_blank" rel="noopener">Terms</a>
      </div>
      <div class="cc-site-footer-meta">
        <div class="cc-site-footer-email">
          <a href="mailto:info@creator-chat.com">info@creator-chat.com</a>
        </div>
        © 2026 Creator Chat. Built for creators who take their work seriously. Hosted in the European Union.
      </div>
    </footer>
  </div>
</template>

<style scoped>
.cc-reset-page {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: #efeae0;
  color: #0b1414;
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
}
.cc-reset-noise {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.35;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.4 0 0 0 0 0.35 0 0 0 0 0.25 0 0 0 0.05 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
}

.cc-reset-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px 36px;
}

.cc-brand-mark { display: flex; align-items: center; }
.cc-brand-icon { display: block; margin-right: 14px; }
.cc-brand-text { line-height: 1; }
.cc-wordmark { line-height: 21px; white-space: nowrap; }
.cc-wordmark-part {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  text-transform: uppercase;
  color: #0b1414;
}
.cc-wordmark-creator { font-weight: 400; }
.cc-wordmark-chat { font-weight: 300; }
.cc-wordmark-dash {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  color: #a8842f;
  font-weight: 400;
}
.cc-wordmark-tld {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: lowercase;
  color: #a8842f;
  vertical-align: 8px;
  padding-left: 2px;
}
.cc-brand-divider { display: block; margin: 6px 0 5px 0; }
.cc-tagline {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 8.5px;
  font-weight: 500;
  letter-spacing: 2.56px;
  color: rgba(11, 20, 20, 0.5);
  text-transform: uppercase;
  line-height: 1;
}

.cc-reset-main {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 24px 40px;
}

.cc-auth-stack {
  width: 100%;
  max-width: 460px;
  display: flex;
  flex-direction: column;
}

.cc-auth-card {
  background: #ffffff;
  border: 1px solid #e0d8c5;
  border-radius: 16px;
  padding: 38px 36px 32px;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.5) inset,
    0 6px 24px rgba(11, 20, 20, 0.04);
}
.cc-auth-card--centered { text-align: center; }

.cc-h-display {
  font-family: 'Cinzel', 'Fraunces', 'Times New Roman', Georgia, serif;
  font-weight: 600;
  color: #0b1414;
  letter-spacing: -0.005em;
  font-size: 26px;
  line-height: 1.2;
  margin: 0 0 6px;
  text-align: center;
}
.cc-sub {
  font-family: 'Inter', sans-serif;
  font-size: 13.5px;
  color: #6e7570;
  line-height: 1.5;
  margin: 0 0 24px;
  text-align: center;
}

.cc-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.cc-field { display: flex; flex-direction: column; }
.cc-label {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #2a3434;
  margin-bottom: 6px;
}
.cc-req { color: #dc2626; font-weight: 800; margin-left: 2px; }

.cc-input {
  width: 100%;
  box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #0b1414;
  padding: 11px 14px;
  border-radius: 10px;
  border: 1.5px solid #e0d8c5;
  background: #fdfcf9;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.cc-input:hover { border-color: #cfc5ac; }
.cc-input:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.22);
}
.cc-input--pwd { padding-right: 70px; }
.cc-input--match {
  border-color: #0d9488;
  background: rgba(13, 148, 136, 0.06);
}

.cc-pwd-wrap { position: relative; }
.cc-pwd-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6e7570;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.cc-pwd-toggle:hover { color: #0b1414; }

.cc-pwd-strength {
  height: 4px;
  border-radius: 2px;
  background: #e0d8c5;
  overflow: hidden;
  margin-top: 6px;
  display: flex;
  gap: 3px;
}
.cc-pwd-strength > div {
  flex: 1;
  background: #e0d8c5;
  border-radius: 2px;
  transition: background 0.2s;
}
.cc-pwd-strength--weak > div:nth-child(1) { background: #dc2626; }
.cc-pwd-strength--medium > div:nth-child(1),
.cc-pwd-strength--medium > div:nth-child(2) { background: #d97706; }
.cc-pwd-strength--strong > div { background: #0d9488; }

.cc-pwd-rules {
  list-style: none;
  padding: 0;
  margin: 4px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.cc-pwd-rules li {
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  line-height: 1.4;
  color: #6e7570;
  transition: color 0.15s;
}
.cc-pwd-rules li svg {
  flex-shrink: 0;
  color: #cfc5ac;
  transition: color 0.15s;
}
.cc-pwd-rules li.cc-pwd-rule--ok {
  color: #0f766e;
  font-weight: 600;
}
.cc-pwd-rules li.cc-pwd-rule--ok svg { color: #0d9488; }

.cc-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 12px 20px;
  border-radius: 10px;
  border: 1.5px solid #0b1414;
  background: #0b1414;
  color: #f5efe0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.15s;
  width: 100%;
  margin-top: 4px;
}
.cc-btn-primary:hover:not(:disabled) {
  background: #142020;
  border-color: #142020;
}
.cc-btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cc-info-panel {
  margin-top: 18px;
  padding: 12px 14px;
  background: #f1ede4;
  border-radius: 9px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #6e7570;
  line-height: 1.5;
}

.cc-card-foot {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #e0d8c5;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #6e7570;
}
.cc-link-accent {
  color: #0f766e;
  font-weight: 600;
  text-decoration: none;
}
.cc-link-accent:hover { text-decoration: underline; }

.cc-status-icon {
  width: 72px;
  height: 72px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}
.cc-status-icon--accent-solid {
  background: #0d9488;
  color: #ffffff;
}

.cc-success-spinner {
  margin: 18px 0 14px;
  display: flex;
  justify-content: center;
}
.cc-spinner {
  animation: cc-spin 0.8s linear infinite;
}
@keyframes cc-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.cc-site-footer {
  position: relative;
  z-index: 1;
  border-top: 1px solid #e0d8c5;
  margin-top: 64px;
  padding: 40px 36px 36px;
}
.cc-site-footer-links {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1100px;
  margin: 0 auto;
}
.cc-site-footer-links a {
  color: #6e7570;
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  transition: color 0.15s;
}
.cc-site-footer-links a:hover { color: #0f766e; }
.cc-site-footer-meta {
  color: #6e7570;
  font-size: 12px;
  margin: 24px auto 0;
  max-width: 1100px;
  text-align: center;
  padding-top: 24px;
  border-top: 1px solid #e0d8c5;
  font-family: 'Inter', sans-serif;
}
.cc-site-footer-meta a {
  color: inherit;
  text-decoration: none;
  font-weight: 600;
}
.cc-site-footer-email { margin-bottom: 6px; }

@media (max-width: 540px) {
  .cc-reset-header { padding: 18px 20px; }
  .cc-reset-main { padding: 16px 16px 32px; }
  .cc-auth-card { padding: 30px 22px 26px; }
  .cc-site-footer { padding: 32px 20px 28px; margin-top: 48px; }
  .cc-site-footer-links { gap: 16px; }
  .cc-tagline { display: none; }
}
</style>
